// app/javascript/controllers/unsubscribe_validation_controller.js
import { Controller } from "@hotwired/stimulus"
import JustValidate from "just-validate"
import Swal from "sweetalert2"

export default class extends Controller {
    static targets = ["email", "error", "choices"]

    connect() {
        this.form = this.element

        this.emailEl = this.hasEmailTarget
            ? this.emailTarget
            : document.getElementById("unsubscribe_email")

        this.errorEl = this.hasErrorTarget
            ? this.errorTarget
            : document.getElementById("unsubscribe-error")

        // Listen for captcha success dispatched by recaptcha_bridge.js
        this.onCaptchaSuccess = (e) => {
            const token = e?.detail?.token
            this.submitAfterCaptcha(token)
        }
        this.form.addEventListener("captcha:success", this.onCaptchaSuccess)

        // JustValidate setup
        this.validator = new JustValidate(this.form, {
            validateBeforeSubmitting: true
        })

        this.validator.addField("#unsubscribe_email", [
            { rule: "required", errorMessage: "Email is required" },
            { rule: "email", errorMessage: "Please enter a valid email address" }
        ])
        this.choicesTarget.hidden = true

        // Create invisible reCAPTCHA widget (uses global callback -> recaptcha_bridge.js)
        this.recaptchaEl = document.querySelector("#unsubscribe-recaptcha")
        if (!this.recaptchaEl) return

        if (
            window.grecaptcha &&
            typeof window.grecaptcha.render === "function" &&
            typeof window.grecaptcha.execute === "function"
        ) {
            this.widgetId = window.grecaptcha.render("unsubscribe-recaptcha", {
                sitekey: this.recaptchaEl.dataset.sitekey,
                size: "invisible",
                callback: window.onUnsubCaptchaSuccess,
                "error-callback": () => this.captchaFailed(),
                "expired-callback": () => this.captchaFailed()
            })
        } else {
            console.warn(
                "reCAPTCHA not ready yet (render/execute unavailable); will render on submit."
            )
        }
    }

    disconnect() {
        this.validator?.destroy()
        if (this.hasChoicesTarget) this.choicesTarget.hidden = false
        if (this.choosingPreferences) Swal.close()

        if (this.form && this.onCaptchaSuccess) {
            this.form.removeEventListener("captcha:success", this.onCaptchaSuccess)
        }

        if (this._recaptchaWaitTimer) {
            clearInterval(this._recaptchaWaitTimer)
            this._recaptchaWaitTimer = null
        }
    }

    waitForRecaptcha(maxMs = 4000, intervalMs = 100) {
        return new Promise((resolve) => {
            const startedAt = Date.now()

            // already loaded (ensure required APIs exist)
            if (
                window.grecaptcha &&
                typeof window.grecaptcha.render === "function" &&
                typeof window.grecaptcha.execute === "function"
            ) {
                return resolve(true)
            }

            this._recaptchaWaitTimer = setInterval(() => {
                if (
                    window.grecaptcha &&
                    typeof window.grecaptcha.render === "function" &&
                    typeof window.grecaptcha.execute === "function"
                ) {
                    clearInterval(this._recaptchaWaitTimer)
                    this._recaptchaWaitTimer = null
                    resolve(true)
                    return
                }

                if (Date.now() - startedAt >= maxMs) {
                    clearInterval(this._recaptchaWaitTimer)
                    this._recaptchaWaitTimer = null
                    resolve(false)
                }
            }, intervalMs)
        })
    }

    async submit(event) {
        event.preventDefault()
        if (this.submitting) return
        this.submitting = true

        try {

            const valid = await this.validator.revalidate().catch(() => false)
            if (!valid) {
                this.showError("Please enter a valid email address.")
                return
            }
            this.hideError()

            const choices = this.choicesTarget.cloneNode(true)
            choices.hidden = false
            choices.removeAttribute("data-unsubscribe-validation-target")
            // Rails-generated radio IDs must not duplicate those in the form.
            choices.querySelectorAll("input").forEach(input => input.removeAttribute("id"))
            this.choosingPreferences = true
            const result = await Swal.fire({
                title: "Which emails would you like to stop?",
                html: choices,
                showCancelButton: true,
                confirmButtonText: "Unsubscribe",
                cancelButtonText: "Cancel",
                confirmButtonColor: "#004770",
                reverseButtons: true,
                focusConfirm: false,
                customClass: { popup: "unsubscribe-preferences-modal" },
                preConfirm: () => Swal.getPopup().querySelector('input[name="partial"]:checked')?.value
            })
            this.choosingPreferences = false
            if (!result.isConfirmed) return
            this.choicesTarget.querySelector(`input[value="${result.value}"]`).checked = true

            // Wait briefly for reCAPTCHA to load (it may load after connect())
            const loaded = await this.waitForRecaptcha()
            if (!loaded) {
                this.showError("Captcha is still loading. Please wait a moment and try again.")
                return
            }

            // If reCAPTCHA loaded after connect(), render it now
            if (this.widgetId === undefined && this.recaptchaEl) {
                this.widgetId = window.grecaptcha.render("unsubscribe-recaptcha", {
                    sitekey: this.recaptchaEl.dataset.sitekey,
                    size: "invisible",
                    callback: window.onUnsubCaptchaSuccess,
                    "error-callback": () => this.captchaFailed(),
                    "expired-callback": () => this.captchaFailed()
                })
            }

            if (this.widgetId === undefined) {
                this.showError("Captcha could not be initialized. Please refresh and try again.")
                return
            }

            window.grecaptcha.reset(this.widgetId)
            this.awaitingCaptcha = true
            window.grecaptcha.execute(this.widgetId)
        } finally {
            if (!this.awaitingCaptcha && !this.saving) this.submitting = false
        }
    }

    submitAfterCaptcha(token) {
        if (!this.awaitingCaptcha) return
        this.awaitingCaptcha = false
        if (!token) {
            this.captchaFailed()
            return
        }

        // token is automatically added by reCAPTCHA to the form
        this.saving = true
        this.showLoader()
        this.form.submit()
    }

    captchaFailed() {
        this.awaitingCaptcha = false
        this.submitting = false
        this.showError("CAPTCHA verification failed. Please try again.")
    }

    showLoader() {
        const loader = this.application.getControllerForElementAndIdentifier(
            this.form,
            "form-loader"
        )
        loader?.showLoader()
    }

    showError(msg) {
        if (!this.errorEl) return
        this.errorEl.textContent = msg
        this.errorEl.classList.remove("d-none")
    }

    hideError() {
        if (!this.errorEl) return
        this.errorEl.classList.add("d-none")
        this.errorEl.textContent = ""
    }
}
